import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { SITE } from '../content';
import { Popups, storage } from '../popups';

/** localStorage: when a coupon was claimed here — the popup then stops opening by itself for CLAIM_DAYS */
const CLAIMED_KEY = 'lnEnqClaimedAt';
const CLAIM_DAYS = 30;
/** it opens by itself this long after the page loads, or once this much of the page has been scrolled */
const AUTO_DELAY_MS = 8000;
const AUTO_SCROLL = 0.45;

const EMAIL = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
const MOBILE = /^[6-9]\d{9}$/;

type CheckedField = 'uname' | 'umobile' | 'uemail' | 'captcha';

/**
 * A stand-in coupon code so that the popup can be shown working. It is made
 * up in the browser: not unique, not single-use, and no checkout knows it.
 * The real code has to come from the server that receives the enquiry.
 */
function demoCoupon(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = 'LN500-';
  for (let i = 0; i < 6; i++) code += chars.charAt(Math.floor(Math.random() * chars.length));
  return code;
}

/**
 * The Enquire Now popup: the enquiry form beside a ₹500 coupon offer, in the
 * centre of the screen. A successful Submit swaps the form for the coupon
 * code. Only the name and the security question are required; phone and
 * email are checked when filled in.
 */
@Component({
  selector: 'app-enquiry-popup',
  templateUrl: './enquiry-popup.html',
  styleUrl: './enquiry-popup.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EnquiryPopup {
  protected readonly popups = inject(Popups);
  protected readonly shopHref = `${SITE}/living-room-recliners`;

  protected readonly open = computed(() => this.popups.open() === 'enquiry');
  /** the security question's two numbers — picked in the browser, so the prerendered page has none */
  protected readonly sum = signal<[number, number] | null>(null);
  /** the fields that failed the last Submit */
  protected readonly errors = signal<Partial<Record<CheckedField, true>>>({});
  /** the line above the Submit button */
  protected readonly problem = signal('');
  /** the coupon code, once the form has gone through: the dialog then shows it in place of the form */
  protected readonly coupon = signal('');
  protected readonly copied = signal(false);

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly message = viewChild<ElementRef<HTMLTextAreaElement>>('message');

  constructor() {
    // the finder's "Talk to an expert" brings its answers along — never over something the visitor typed
    effect(() => {
      const box = this.message()?.nativeElement;
      const note = this.popups.note();
      if (this.open() && box && note && !box.value) box.value = note;
    });

    afterNextRender(() => {
      this.sum.set([Math.floor(Math.random() * 10), Math.floor(Math.random() * 10)]);

      const claimedAt = Number(storage.get('local', CLAIMED_KEY));
      if (claimedAt && Date.now() - claimedAt < CLAIM_DAYS * 864e5) return;
      this.popups.autoOpen('enquiry', AUTO_DELAY_MS, AUTO_SCROLL);
    });
  }

  /**
   * This page is static: there is no backend, so the enquiry is NOT sent
   * anywhere and the coupon is a stand-in (see demoCoupon). Post `data` to
   * the enquiry API here when one is available, check the security question
   * there too, and show the coupon code that comes back.
   */
  protected submit(event: SubmitEvent): void {
    event.preventDefault();
    const data = new FormData(event.target as HTMLFormElement);
    const value = (name: string) => String(data.get(name) ?? '').trim();

    const errors: Partial<Record<CheckedField, true>> = {};
    if (!value('uname')) errors.uname = true;
    if (value('umobile') && !MOBILE.test(value('umobile'))) errors.umobile = true;
    if (value('uemail') && !EMAIL.test(value('uemail'))) errors.uemail = true;
    const [a, b] = this.sum() ?? [0, 0];
    if (value('captcha') === '' || parseInt(value('captcha'), 10) !== a + b) errors.captcha = true;

    this.errors.set(errors);
    this.problem.set(errors.captcha ? 'Please answer the security question correctly.' : '');
    if (Object.keys(errors).length) return;

    this.coupon.set(demoCoupon());
    this.popups.claimed.set(true);
    storage.set('local', CLAIMED_KEY, String(Date.now()));
    // once the coupon is on screen: from its top, with focus on Copy
    setTimeout(() => {
      this.host.querySelector('.enquiry__main')?.scrollTo(0, 0);
      this.copyButton()?.focus();
    }, 60);
  }

  private copyButton(): HTMLElement | null {
    return this.host.querySelector<HTMLElement>('.code__copy');
  }

  protected async copy(code: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // no clipboard access (an http address, an old browser): the long way round
      const box = document.createElement('textarea');
      box.value = code;
      document.body.appendChild(box);
      box.select();
      try {
        document.execCommand('copy');
      } catch {
        // the code stays on screen to copy by hand
      }
      box.remove();
      this.copyButton()?.focus();
    }
    this.copied.set(true);
    setTimeout(() => this.copied.set(false), 2000);
  }
}
